import {
  compareReleaseIds,
  getAllReleases,
  getCurrentReleaseManifest,
} from "./releaseManifest";

export function getAllFeatureEntries() {
  const entries = [];

  for (const { manifest } of getAllReleases()) {
    if (manifest?.features) {
      entries.push(...manifest.features);
    }
  }

  return entries;
}

export function getFeaturesForRelease(releaseId) {
  const manifest = getCurrentReleaseManifest(releaseId);
  return manifest?.features ? [...manifest.features] : [];
}

export function getBundlesForRelease(releaseId) {
  const manifest = getCurrentReleaseManifest(releaseId);
  return manifest?.bundles ? [...manifest.bundles] : [];
}

export function getFeatureById(featureId) {
  return getAllFeatureEntries().find((feature) => feature.id === featureId) || null;
}

export function getBundleById(releaseId, bundleId) {
  return getBundlesForRelease(releaseId).find((bundle) => bundle.id === bundleId) || null;
}

/**
 * Features unseen since lastSeenReleaseId, ordered by bundle then feature displayOrder.
 */
export function getUnseenFeatures(lastSeenReleaseId, dismissedFeatureIds = []) {
  const dismissed = new Set(dismissedFeatureIds);
  const unseen = getAllFeatureEntries().filter((feature) => {
    if (dismissed.has(feature.id)) {
      return false;
    }
    if (!lastSeenReleaseId) {
      return true;
    }
    return compareReleaseIds(feature.releaseId, lastSeenReleaseId) > 0;
  });

  return sortFeaturesByBundleOrder(unseen);
}

export function getTourWorthyFeatures(features) {
  return features.filter(
    (feature) =>
      (feature.severity === "major" || feature.severity === "flagship") &&
      feature.tour?.include !== false,
  );
}

export function sortFeaturesByBundleOrder(features) {
  const byRelease = new Map();

  for (const feature of features) {
    if (!byRelease.has(feature.releaseId)) {
      byRelease.set(feature.releaseId, []);
    }
    byRelease.get(feature.releaseId).push(feature);
  }

  const sorted = [];

  for (const [, releaseFeatures] of byRelease) {
    const releaseId = releaseFeatures[0]?.releaseId;
    const bundles = getBundlesForRelease(releaseId);
    const bundleOrder = new Map(
      bundles.map((bundle) => [bundle.id, bundle.displayOrder]),
    );

    releaseFeatures.sort((a, b) => {
      const bundleDiff =
        (bundleOrder.get(a.bundleId) ?? 0) - (bundleOrder.get(b.bundleId) ?? 0);
      if (bundleDiff !== 0) {
        return bundleDiff;
      }
      return a.displayOrder - b.displayOrder;
    });

    sorted.push(...releaseFeatures);
  }

  return sorted;
}

export function groupFeaturesByBundle(releaseId, features) {
  const bundles = getBundlesForRelease(releaseId);
  const bundleMap = new Map(bundles.map((bundle) => [bundle.id, { ...bundle, features: [] }]));

  for (const feature of features) {
    if (feature.releaseId !== releaseId) {
      continue;
    }
    const bucket = bundleMap.get(feature.bundleId);
    if (bucket) {
      bucket.features.push(feature);
    }
  }

  return [...bundleMap.values()]
    .filter((bundle) => bundle.features.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}
